/**
 * Buyer-facing message shown after the validator removes or trims a gift.
 *
 * Must match shared-cart/constants.ts UserMessages.GIFT_REMOVED — duplicated because
 * top-level modules are self-contained (cross-importing shared-cart breaks the
 * IIFE-wrap constraint by emitting shared chunks; see cart-region-validator/cart.ts).
 * Parity-tested in __tests__/ui.test.ts.
 */
export const GIFT_REMOVED_MESSAGE =
  'A free gift was removed because your cart no longer qualifies for this offer.';

/**
 * Mirrors shared-cart/ui/error.ts displayError (same self-contained-entry rationale):
 * prefers the cart page's #cart-errors, falls back to the drawer's
 * #CartDrawer-CartErrors. Both exist on /cart when the drawer is enabled (the drawer is
 * rendered on every page), so the preference is a precedence rule: on the cart page the
 * inline #cart-errors is the visible node, the (closed) drawer's alert stays untouched;
 * everywhere else only the drawer node exists.
 */
export function displayError(message: string): void {
  const errorEl =
    document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
  if (errorEl) {
    errorEl.textContent = message;
  }
}

/**
 * Retracts a standing gift-removed notice once the cart validates clean again (the
 * customer re-qualified, or removed the gift themselves) so it cannot go stale. Both
 * error nodes are checked — displayError may have written to either depending on the
 * page. A node is cleared ONLY when it holds exactly GIFT_REMOVED_MESSAGE: another
 * producer's message is never clobbered, and an already-empty role="alert" node is never
 * rewritten (mutating it could re-announce to screen readers).
 */
export function clearGiftRemovedMessage(): void {
  for (const id of ['cart-errors', 'CartDrawer-CartErrors']) {
    const el = document.getElementById(id);
    if (el && el.textContent === GIFT_REMOVED_MESSAGE) {
      el.textContent = '';
    }
  }
}

export function refreshCartUI(): void {
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    void publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp-validator' });
  } else {
    console.warn('[GWP Validator] Theme pub/sub not available, falling back to cart:updated event');
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }
}

/**
 * Header cart bubble section id. Doubles as the section name requested from
 * /cart/update.js and the DOM id of the node swapped in from the response.
 */
export const CART_ICON_BUBBLE_SECTION = 'cart-icon-bubble';

// Swap the header cart bubble from a section-rendered update.js response. Dawn's
// cartUpdate subscribers only re-render cart line items, so after a validator-driven
// gift removal the header count would otherwise stay stale until the next page load.
// Mirrors Dawn's section swap (assets/cart.js CartItems.updateQuantity).
export function renderCartIconBubble(
  sections: Record<string, string | null> | null | undefined,
): void {
  const html = sections?.[CART_ICON_BUBBLE_SECTION];
  const bubble = document.getElementById(CART_ICON_BUBBLE_SECTION);
  if (typeof html !== 'string' || !bubble) return;

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const source = parsed.querySelector('.shopify-section') || parsed.body;
  const target = bubble.querySelector('.shopify-section') || bubble;
  target.innerHTML = source.innerHTML;
}
